import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Chip,
    Grid,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    Menu,
    MenuItem,
    IconButton
} from '@mui/material';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import InventoryOrderService from '@api/InventoryOrders/InventoryOrderCtrl';
import {
    InventoryOrder,
    getInventoryOrderStatusName,
    getInventoryOrderStatusColor,
    getInventoryOrderPaymentStatusName,
    getInventoryOrderPaymentStatusColor
} from '@api/InventoryOrders/InventoryOrder';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ResponsiveDataGrid, ResponsiveGridColDef } from '@components/Utilities/Tables/ResponsiveDataGrid';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { setActivityDrawer } from '@store/appStore';
import { buildInventoryOrderOrigin } from '@api/ActivityLogs/InventoryOrderActivity';
import { useDispatch } from 'react-redux';

interface ListInventoryOrdersProps {
    branchOfficeId: string;
}
const ListInventoryOrders: React.FunctionComponent<ListInventoryOrdersProps> = (props) => {
    const dispatch = useDispatch();
    const { branchOfficeId } = props;
    const navigation = useNavigate();
    const inventoryOrderService = useMemo(() => new InventoryOrderService(), []);
    const [inventoryOrders, setInventoryOrders] = useState<InventoryOrder[]>([]);
    const [filters, setFilters] = useState({ search: '' });
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedOrder, setSelectedOrder] = useState<InventoryOrder | null>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: InventoryOrder) => {
        setAnchorEl(event.currentTarget);
        setSelectedOrder(row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedOrder(null);
    };

    const handleView = () => {
        if (selectedOrder) {
            void onShowInventoryOrder(selectedOrder);
        }
        handleMenuClose();
    };

    /**
     * La bitácora del pedido, también del lado de la SUCURSAL: aquí es donde se
     * marca "Recibido", que es el momento irreversible del circuito (M6).
     */
    const handleActivity = () => {
        const order = selectedOrder;
        handleMenuClose();
        if (!order?.id) return;
        dispatch(
            setActivityDrawer({
                isOpen: true,
                section: 'inventoryOrder',
                sectionId: order.id,
                derived: buildInventoryOrderOrigin(order)
            })
        );
    };

    const getData = async () => {
        showLoader();
        try {
            const data = await inventoryOrderService.getInventoryOrders(branchOfficeId);
            setInventoryOrders(data);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onShowInventoryOrder = async (inventoryOrder: InventoryOrder) => {
        navigation('pos', { state: { inventoryOrderId: inventoryOrder.id } });
    };

    const getTotal = (inventoryOrder: InventoryOrder) => {
        return inventoryOrder.items
            .reduce((a, inventoryOrder) => a + (inventoryOrder.quantity || 0) * inventoryOrder.input.costOfSale, 0)
            .toFixed(2);
    };

    useEffect(() => {
        void getData();
    }, []);

    const columns = useMemo<ResponsiveGridColDef<InventoryOrder>[]>(
        () => [
            {
                field: 'id',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        ID de pedido
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.shortId || row.id}</Typography>,
                valueGetter: ({ row }) => row.shortId || row.id
            },
            {
                field: 'Fecha',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Fecha
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{format(row.createdAt.toDate(), 'dd-MM-yyy pp')}</Typography>
            },
            {
                field: 'createdBy',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Creado por
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.createdBy?.profile?.name || 'Desconocido'}</Typography>,
                valueGetter: ({ row }) => row.createdBy?.profile?.name || 'Desconocido'
            },
            {
                field: 'items',
                flex: 1 / 2,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Artículos
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.items.length}</Typography>,
                valueGetter: ({ row }) => row.items.length
            },
            {
                field: 'total',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Total
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>${getTotal(row)}</Typography>,
                valueGetter: ({ row }) => getTotal(row)
            },
            {
                field: 'paymentStatus',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Estado del pago
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Chip
                        label={getInventoryOrderPaymentStatusName(row.paymentStatus)}
                        color={getInventoryOrderPaymentStatusColor(row.paymentStatus)}
                        size="small"
                    />
                ),
                valueGetter: ({ row }) => getInventoryOrderPaymentStatusName(row.paymentStatus)
            },
            {
                field: 'status',
                flex: 1,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Estado
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Chip
                        label={getInventoryOrderStatusName(row.status)}
                        color={getInventoryOrderStatusColor(row.status)}
                        size="small"
                    />
                ),
                valueGetter: ({ row }) => getInventoryOrderStatusName(row.status)
            },
            {
                field: 'actions',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                headerAlign: 'center',
                align: 'center',
                renderHeader: () => <SettingsIcon sx={{ color: '#809FB8' }} />,
                renderCell: ({ row }) => (
                    <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                        <MoreVertIcon />
                    </IconButton>
                )
            }
        ],
        []
    );

    return (
        <Stack height="100%" spacing={2}>
            <Box>
                <Grid container spacing={2}>
                    <Grid item xs={9} />
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Buscar pedido"
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
            <ResponsiveDataGrid
                searchValue={filters.search}
                rows={inventoryOrders}
                columns={columns}
                onCellDoubleClick={(cel) => onShowInventoryOrder(cel.row as InventoryOrder)}
            />
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleView}>Ver</MenuItem>
                <MenuItem onClick={handleActivity}>Actividad</MenuItem>
            </Menu>
        </Stack>
    );
};
export default ListInventoryOrders;
